import {Box, Stack, Tooltip, Typography} from "@mui/material";
import type {ContentFramePointer, ODIPointer} from "../../../../../sdo/ContentFramePointer.ts";

interface ContentFrameCardProps {
    frame: ContentFramePointer;
}

function positionShort(f: ContentFramePointer): string {
    return `${f.page}:${f.section}:${f.paragraph}:${f.sentence}`;
}

function positionLong(f: ContentFramePointer): string {
    return `Page ${f.page} · Section ${f.section} · Paragraph ${f.paragraph} · Sentence ${f.sentence}`;
}

function PointerPill({pointer}: { pointer: ODIPointer }) {
    return (
        <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            padding: "2px 8px",
            borderRadius: "10px",
            border: "1px solid rgba(112, 147, 160, 0.35)",
            backgroundColor: "rgba(26, 37, 48, 0.85)",
        }}>
            <img
                src={`/images/intel/${pointer.typeKey}.svg`}
                width={14}
                height={14}
                alt=""
                aria-hidden="true"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <Typography sx={{color: "#ffffff", fontSize: "0.70rem", textAlign: "left"}}>
                {pointer.display}
            </Typography>
        </Box>
    );
}

function ContentFrameCard({frame}: ContentFrameCardProps) {
    return (
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            background: "linear-gradient(155deg, rgba(0, 71, 106, 0.30) 0%, rgba(0, 38, 53, 0.30) 25%, rgba(0, 0, 0, 0.30) 90%, rgba(0, 0, 0, 0.40) 100%)",
            borderRadius: "12px",
            border: "0.5px solid #3F464F",
            overflow: "hidden",
        }}>
            {/* Left: frame icon */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 10px",
            }}>
                <img src="/images/content_pages.svg" width={22} height={22} alt="Frame" style={{opacity: 0.85}}/>
            </Box>

            {/* Center: header + body */}
            <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", padding: "6px 10px 8px 4px"}}>
                {/* Header row */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{width: "100%"}}>
                    <Typography sx={{
                        color: "#f2f5fa",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        textAlign: "left",
                    }}>
                        Frame {frame.frameNumber}
                    </Typography>
                    <Tooltip title={positionLong(frame)} arrow placement="left">
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.55)",
                            fontFamily: "monospace",
                            fontSize: "0.70rem",
                            textAlign: "right",
                        }}>
                            {positionShort(frame)}
                        </Typography>
                    </Tooltip>
                </Stack>

                {/* Original text */}
                {frame.originalText && frame.originalText.trim().length > 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.85)",
                        fontSize: "0.75rem",
                        fontWeight: 300,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        textAlign: "left",
                        marginTop: "4px",
                    }}>
                        {frame.originalText}
                    </Typography>
                )}

                {/* Pointer pills */}
                {frame.pointers && frame.pointers.length > 0 && (
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: "6px",
                    }}>
                        {frame.pointers.map((p) => (
                            <PointerPill key={p.uid} pointer={p}/>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ContentFrameCard;
